<template>
    <div v-if="images.length >= 1" ref="viewerContainer"
        class="mb-5 mt-6 md:my-10 rounded-lg h-[40vh] md2:h-[50vh] overflow-hidden relative fade-in animate-in" v-viewer="viewerOptions">
        <div class="grid sm:grid-cols-2 gap-2 w-full h-full h-[40vh] md2:h-[50vh]">

            <!-- Left side: either video or main image -->
            <div class="relative w-full h-full overflow-hidden rounded-xl h-[40vh] md2:h-[50vh]">
                <div v-if="videoUrl" class="bg-video h-full w-full">
                    <iframe class="bg-video__iframe"
                        :src="`${videoUrl.replace('watch?v=', 'embed/')}?autoplay=1&mute=1&loop=1&playlist=${extractYouTubeId(videoUrl)}&controls=0&modestbranding=1&showinfo=0&rel=0&iv_load_policy=3&fs=0&disablekb=1&playsinline=1`"
                        title="Property Video" frameborder="0" allow="autoplay; encrypted-media; fullscreen"
                        allowfullscreen>
                    </iframe>
                </div>
                <vue-load-image v-else-if="images[0]" class="h-full">
                    <img slot="image"
                        class="w-full h-full object-cover rounded-xl animate-in fade-in duration-300 hover:scale-95 active:scale-75"
                        :src="mipmap(images[0].fullUrl, 800)" :alt="images[0].alt || ''" :data-href="images[0].href"
                        :key="'accopage' + images[0].src" loading="eager" />
                    <img slot="preloader"
                        class="w-full h-full object-cover animate-pulse blur-lg bg-gray-300 dark:bg-neutral-700 rounded-xl"
                        :src="mipmap(images[0].fullUrl, 10)" :data-href="images[0].href"
                        :key="'accopage-preload' + images[0].src" loading="eager" />
                </vue-load-image>
            </div>

            <!-- Right side: grid of up to 4 images -->
            <div class="hidden sm:grid gap-2 h-full w-full min-h-0 overflow-hidden h-[40vh] md2:h-[50vh]" :class="{
                'grid-cols-1 grid-rows-1': imageCount === 1,
                'grid-cols-2 grid-rows-1': imageCount === 2,
                'grid-cols-3 grid-rows-1': imageCount === 3,
                'grid-cols-2 grid-rows-2': imageCount >= 4
            }">
                <vue-load-image v-for="(image, index) in visibleImages" :key="'accogrid' + index + '_' + image.src"
                    class="w-full h-full overflow-hidden max-h-[40vh] md2:max-h-[50vh]">
                    <img slot="image"
                        class="w-full h-full object-cover rounded-xl animate-in fade-in duration-300 hover:scale-95 active:scale-75 unselectable"
                        :src="mipmap(image.fullUrl, 400)" :alt="image.alt || ''" :data-href="image.href"
                        :key="'accogridimg' + index + '_' + image.src" loading="eager" />
                    <img slot="preloader"
                        class="w-full h-full object-cover animate-pulse blur-lg bg-gray-300 dark:bg-neutral-700 rounded-xl"
                        :src="mipmap(image.fullUrl, 10)" :data-href="image.href"
                        :key="'accogridpre' + index + '_' + image.src" loading="lazy" />
                </vue-load-image>
            </div>
        </div>

        <!-- Optional thumbnails -->
        <div v-if="images.length > 5" class="flex flex-wrap gap-2 mt-2">
            <img v-for="(image, index) in images.slice(5)" :key="'rest' + index + '_' + image.src"
                class="w-24 h-24 object-cover rounded-md" :src="image.src" :alt="image.alt || ''" :data-href="image.href"
                loading="lazy" />
        </div>

        <div v-if="images.length" @click="openViewer"
            class="absolute right-2 bottom-2 bg-white/50 backdrop-blur rounded text-xs p-1 cursor-pointer">
            {{ $t('Show all') }}
        </div>
    </div>
</template>
  
<script>
import VueLoadImage from 'vue-load-image'
import Viewer from 'v-viewer'
import Vue from 'vue'
import 'viewerjs/dist/viewer.css'

Vue.use(Viewer)

export default {
    name: 'ImageViewer',
    components: { VueLoadImage },

    props: {
        rawImages: { type: Array, default: () => [] },
        videoUrl: { type: String, default: null },
    },

    data() {
        return {
            viewerOptions: {
                movable: true,
                rotatable: true,
                scalable: true,
                url: 'data-href',
                title(image) {
                    return image.alt
                },
            },
        }
    },

    computed: {
        images() {
            return this.rawImages.map(img => ({
                src: img.thumb,
                href: img.large,
                fullUrl: img.medium,
                alt: img.file_name || '',
            }))
        },
        visibleImages() {
            return this.videoUrl ? this.images.slice(0, 4) : this.images.slice(1, 5)
        },
        imageCount() {
            return this.visibleImages.length
        },
    },

    methods: {
        mipmap(url, size) {
            if (!url) return ''
            return url
        },
        extractYouTubeId(url) {
            const match = url.match(/(?:v=|\/)([0-9A-Za-z_-]{11})/)
            return match ? match[1] : ''
        },
        openViewer() {
            if (this.$refs.viewerContainer?.$viewer) {
                this.$refs.viewerContainer.$viewer.show()
            }
        },
    },
}
</script>